/**
 * Module: `queries/current-user.ts` — shared session user fetch for router + UI.
 */

import { queryOptions } from "@tanstack/react-query";

import { API_ENDPOINTS } from "@/constants";
import { CURRENT_USER_QUERY_KEY } from "@/constants/current-user-query-keys";
import { appErrorFromResponseBody } from "@/lib/errors/app-error";
import type { DbUser } from "@/types";

export class CurrentUserUnauthorizedError extends Error {
  constructor() {
    super("Unauthorized");
    this.name = "CurrentUserUnauthorizedError";
  }
}

export class CurrentUserBetaAccessDeniedError extends Error {
  constructor() {
    super("Beta access denied");
    this.name = "CurrentUserBetaAccessDeniedError";
  }
}

export function isCurrentUserUnauthorized(error: unknown): boolean {
  return error instanceof CurrentUserUnauthorizedError;
}

export function isCurrentUserBetaAccessDenied(error: unknown): boolean {
  return error instanceof CurrentUserBetaAccessDeniedError;
}

async function fetchCurrentUser(): Promise<DbUser> {
  const response = await fetch(API_ENDPOINTS.CURRENT_USER, {
    credentials: "include",
  });

  if (response.status === 401) {
    throw new CurrentUserUnauthorizedError();
  }

  if (response.status === 403) {
    const errorData = (await response.json().catch(() => null)) as {
      error?: { type?: string };
    } | null;
    if (errorData?.error?.type === "beta_access_denied") {
      throw new CurrentUserBetaAccessDeniedError();
    }
  }

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw appErrorFromResponseBody(response.status, errorData);
  }

  return response.json() as Promise<DbUser>;
}

export function currentUserQueryOptions() {
  return queryOptions({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: fetchCurrentUser,
    staleTime: 60_000,
  });
}
